<template>
	<div
		class="goal-picker"
		:class="wraperClass"
	>
		<!-- header & subheader -->
		<div class="header">
			<p class="title">
				{{ labels.title }}
			</p>
			<button @click="handleCancel">
				<Icon
					icon-name="e-remove"
					size="lg"
				/>
			</button>
		</div>
		<div class="subheader">
			<p>{{ labels.subtitle }}</p>
		</div>
		<!-- search box -->
		<div class="search">
			<div class="input">
				<input
					v-model="searchQuery"
					type="text"
					:placeholder="labels.searchPlaceholder"
				>
				<button
					v-if="searchQuery.length > 0"
					class="button-clear"
					@click="handleClear"
				>
					<Icon
						class="icon"
						icon-name="e-remove"
					/>
				</button>
				<Icon
					v-else
					class="icon"
					icon-name="search"
				/>
			</div>
		</div>
		<!-- goal list -->
		<ul>
			<li
				v-for="item in filteredItems"
				:key="`goal-picker-item-${item.id}`"
				@click="handleItemClick(item)"
			>
				<!-- indicator -->
				<div
					class="indicator"
					:class="{ current: item === selectedItem }"
				>
					<div class="indicator-fill" />
				</div>
				<div class="icon">
					<Icon
						v-if="item.icon"
						:icon-name="item.icon"
					/>
				</div>
				<!-- details -->
				<div
					v-if="item.playerName"
					class="player-info"
				>
					<div class="player-name">
						{{ item.playerName }}
					</div>
					<div class="player-number">
						<span v-if="item.playerNumber">#{{ item.playerNumber }}</span>
					</div>
				</div>
				<div
					v-else
					class="tag-name"
				>
					<span>{{ item.tagName }}</span>
				</div>
				<div class="team">
					{{ item.team }}
				</div>
				<div class="time">
					{{ item.time }}
				</div>
			</li>
		</ul>
		<!-- actions -->
		<div
			class="actions"
			:class="{ disabled: !hasSelection }"
		>
			<button
				class="delete"
				@click="handleConfirm"
			>
				{{ labels.deleteAction }}
			</button>
		</div>
	</div>
</template>

<script>
import Icon from '@components/Icon';

const VARIANT = {
	DEFAULT: 'default',
	MOBILE:  'mobile',
};

export default {
	name:       'GoalPicker',
	components: { Icon },
	props:      {
		items: {
			type:    Array, // { id, playerName, playerNumber, team, time, icon }[]
			default: () => [],
		},
		variant: {
			type:    String,
			default: VARIANT.DEFAULT,
		},
	},
	data() {
		return {
			searchQuery:  '',
			selectedItem: null,
		};
	},
	computed: {
		isMobileVariant() {
			return this.variant === VARIANT.MOBILE;
		},
		wraperClass() {
			return {
				mobile: this.isMobileVariant,
			};
		},
		labels() {
			return {
				title:             this.$I18n.trans('one.goal_delete_picker_title'),
				subtitle:          this.$I18n.trans('one.goal_delete_picker_subtitle'),
				searchPlaceholder: this.$I18n.trans('one.goal_delete_picker_search_placeholder'),
				deleteAction:      this.$I18n.trans('one.goal_delete_picker_delete_action'),
			};
		},
		filteredItems() {
			if (!this.searchQuery) return this.items;
			return this.items.filter((item) => {
				const fieldValues = Object.values(item).map((fieldValue) => `${fieldValue}`.toLowerCase());
				return fieldValues.some((fieldValue) => fieldValue.includes(this.searchQuery.toLowerCase()));
			});
		},
		hasSelection() {
			return this.selectedItem !== null;
		},
	},
	methods: {
		handleCancel() {
			this.$emit('cancel');
		},
		handleConfirm() {
			this.$emit('confirm', this.selectedItem);
		},
		handleItemClick(item) {
			this.selectedItem = item;
		},
		handleClear() {
			this.searchQuery = '';
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@shared/sass/shared-variables";

.goal-picker {
	flex: 1;
	display: flex;
	flex-direction: column;
	background: var(--color-grey-900);
	height: 100%;

	.header {
		display: flex;
		padding: 24px;

		.title {
			font-size: 24px;
		}
	}

	.subheader {
		padding: 8px 24px;
	}

	.search {
		padding: 24px;

		.input {
			position: relative;

			> .icon,
			> button {
				position: absolute;
				top: 12px;
				right: 12px;
			}

			input {
				width: 100%;
				background: var(--color-black);
				border-radius: 4px;
				padding: 12px 48px 12px 16px;
				line-height: normal;
				outline: 1px solid var(--color-grey-850);

				&:focus {
					outline: 1px solid var(--color-hype-yellow);
				}
			}
		}
	}

	ul {
		flex: 1;
		border-top: 1px solid var(--color-grey-850);
		border-bottom: 1px solid var(--color-grey-850);
		font-size: 14px;
		line-height: normal;
		overflow: auto;

		li {
			display: flex;
			padding: 16px 24px;
			cursor: pointer;
			align-items: center;

			&:hover {
				background: --lighten-color-grey-900;
			}
		}

		li + li {
			border-top: 1px solid var(--color-grey-850);
		}

		.indicator {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 16px;
			height: 16px;
			margin-right: 16px;
			border-radius: 100%;
			border: 1px solid var(--color-grey-300);

			.indicator-fill {
				display: none;
				width: 8px;
				height: 8px;
				background: var(--color-hype-yellow);
				border-radius: 100%;
			}

			&.current {
				border: 1px solid var(--color-hype-yellow);
				.indicator-fill {
					display: block;
				}
			}
		}

		.player-number,
		.team,
		.time {
			min-width: 10%;
			padding: 0 24px;
		}

		.time {
			min-width: max(10%, 60px);
		}

		.icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 16px;
			height: 16px;
			margin-right: 10px;
		}
		.player-info,
		.tag-name {
			flex: 1;
		}
		.player-number {
			text-align: center;
		}
		.team {
			text-align: left;
		}
		.time {
			padding-right: 0;
			text-align: right;
		}
	}

	.actions {
		display: flex;
		padding: 24px;

		&.disabled {
			pointer-events: none;
			filter: grayscale(1);
		}

		button {
			font-size: 16px;
		}

		button.delete {
			flex: 1;
			padding: 14px;
			border-radius: 2px;
			text-align: center;
			background: var(--color-red);

			&:hover {
				background: --lighten-color-red;
			}
		}
	}
}

.goal-picker.mobile {
	.header {
		padding: 24px 16px;
		border-bottom: 1px solid var(--color-grey-850);
	}
	.subheader {
		display: none;
	}
	.search {
		padding: 24px 16px;

		input {
			padding: 10px 16px 10px 16px;
			font-size: 16px;
		}

		.icon {
			width: 16px;
			height: 16px;
		}
	}
	.actions {
		padding: 16px;
	}

	ul li {
		padding: 16px;
	}
}
</style>
